import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Relatorios.module.css';
import { listar, COLECOES } from '../../services/firestore';
import { exportarPDF, exportarExcel } from '../../utils/exportar';

// ============================================================
// RELATÓRIO: MOVIMENTAÇÕES POR PERÍODO
// ============================================================
// Lista entradas, saídas, devoluções e transferências dentro
// de um intervalo de datas. Cada movimentação pode ter vários
// itens — geramos uma linha por item (mais útil para Excel).
// ============================================================

const TIPOS = {
  SAIDA: { label: 'Saída', classe: 'badgeSaida' },
  ENTRADA: { label: 'Entrada', classe: 'badgeEntrada' },
  DEVOLUCAO: { label: 'Devolução', classe: 'badgeDevolucao' },
  TRANSFERENCIA: { label: 'Transferência', classe: 'badgeTransferencia' },
};

function dataParaInicioDoDia(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0);
}
function dataParaFimDoDia(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59);
}

function tsParaDate(ts) {
  if (!ts) return null;
  try {
    return typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
  } catch {
    return null;
  }
}

export default function RelMovimentacoes() {
  const navigate = useNavigate();

  const [movimentacoes, setMovimentacoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const hoje = new Date().toISOString().slice(0, 10);
  const trintaAtras = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  const [dataInicio, setDataInicio] = useState(trintaAtras);
  const [dataFim, setDataFim] = useState(hoje);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [busca, setBusca] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const movs = await listar(COLECOES.MOVIMENTACOES);
      setMovimentacoes(movs);
    } catch (e) {
      setErro(e.message || 'Erro ao carregar movimentações.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const linhas = useMemo(() => {
    const inicio = dataParaInicioDoDia(dataInicio);
    const fim = dataParaFimDoDia(dataFim);

    const r = [];
    movimentacoes.forEach((m) => {
      const data = tsParaDate(m.criadoEm || m.criado_em);
      if (!data) return;
      if (inicio && data < inicio) return;
      if (fim && data > fim) return;

      if (filtroTipo && m.tipo !== filtroTipo) return;
      if (filtroStatus && m.status !== filtroStatus) return;

      const dataStr = data.toLocaleDateString('pt-BR');

      const itens = Array.isArray(m.itens) && m.itens.length > 0 ? m.itens : [
        {
          produtoNome: m.produto || m.produtoNome || '—',
          qtd: m.quantidade ?? m.qtd ?? '',
        },
      ];

      itens.forEach((it) => {
        const produto = it.cor || it.produtoNome || it.nome || '';
        if (busca) {
          const q = busca.toLowerCase();
          const match =
            produto.toLowerCase().includes(q) ||
            (m.os || '').toLowerCase().includes(q) ||
            (m.estoqueOrigemNome || '').toLowerCase().includes(q) ||
            (m.estoqueDestinoNome || '').toLowerCase().includes(q);
          if (!match) return;
        }

        r.push({
          data: dataStr,
          dataObj: data,
          tipo: m.tipo,
          tipoLabel: TIPOS[m.tipo]?.label || m.tipo,
          produto,
          cor: it.cor || '',
          qtd: it.qtd ?? 0,
          origem: m.estoqueOrigemNome || '',
          destino: m.estoqueDestinoNome || '',
          os: m.os || '',
          status: m.status || '',
          responsavel: m.responsavelNome || m.responsavelEmail || '',
        });
      });
    });

    return r.sort((a, b) => b.dataObj - a.dataObj);
  }, [movimentacoes, dataInicio, dataFim, filtroTipo, filtroStatus, busca]);

  const resumo = useMemo(() => {
    const total = linhas.length;
    const saidas = linhas.filter((l) => l.tipo === 'SAIDA').length;
    const entradas = linhas.filter((l) => l.tipo === 'ENTRADA').length;
    const devolucoes = linhas.filter((l) => l.tipo === 'DEVOLUCAO').length;
    const transferencias = linhas.filter((l) => l.tipo === 'TRANSFERENCIA').length;
    return { total, saidas, entradas, devolucoes, transferencias };
  }, [linhas]);

  const colunasExport = [
    { header: 'Data', dataKey: 'data' },
    { header: 'Tipo', dataKey: 'tipoLabel' },
    { header: 'Produto', dataKey: 'produto' },
    { header: 'Cor', dataKey: 'cor' },
    { header: 'Qtd (L)', dataKey: 'qtd' },
    { header: 'Origem', dataKey: 'origem' },
    { header: 'Destino', dataKey: 'destino' },
    { header: 'OS', dataKey: 'os' },
    { header: 'Status', dataKey: 'status' },
    { header: 'Responsável', dataKey: 'responsavel' },
  ];

  function handlePDF() {
    const sub = `Período: ${dataInicio.split('-').reverse().join('/')} a ${dataFim
      .split('-')
      .reverse()
      .join('/')}`;
    exportarPDF({
      titulo: 'Movimentações',
      subtitulo: sub,
      colunas: colunasExport,
      dados: linhas,
      orientacao: 'landscape',
    });
  }

  function handleExcel() {
    exportarExcel({
      titulo: 'Movimentações',
      colunas: colunasExport,
      dados: linhas,
      nomeAba: 'Movimentações',
    });
  }

  return (
    <>
      <div className={styles.topbar}>
        <div>
          <div className={styles.pageTitle}>Movimentações por Período</div>
          <div className={styles.pageSub}>
            Histórico de entradas, saídas, devoluções e transferências.
          </div>
        </div>
        <button
          className={styles.btnVoltar}
          onClick={() => navigate('/relatorios')}
        >
          ← Voltar para Relatórios
        </button>
      </div>

      {erro && (
        <div
          style={{
            background: '#fdecea',
            color: '#b51013',
            border: '1px solid #f5c6cb',
            borderRadius: 8,
            padding: '10px 14px',
            margin: '14px 0',
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      <div className={styles.resumo} style={{ marginTop: 14 }}>
        <div className={styles.resumoCard}>
          <div className={styles.resumoLabel}>Total</div>
          <div className={styles.resumoValor}>{resumo.total}</div>
        </div>
        <div className={styles.resumoCard}>
          <div className={styles.resumoLabel}>Saídas</div>
          <div className={`${styles.resumoValor} ${styles.warn}`}>
            {resumo.saidas}
          </div>
        </div>
        <div className={styles.resumoCard}>
          <div className={styles.resumoLabel}>Entradas</div>
          <div className={`${styles.resumoValor} ${styles.ok}`}>
            {resumo.entradas}
          </div>
        </div>
        <div className={styles.resumoCard}>
          <div className={styles.resumoLabel}>Devoluções</div>
          <div className={styles.resumoValor}>{resumo.devolucoes}</div>
        </div>
        <div className={styles.resumoCard}>
          <div className={styles.resumoLabel}>Transferências</div>
          <div className={`${styles.resumoValor} ${styles.info}`}>
            {resumo.transferencias}
          </div>
        </div>
      </div>

      <div className={styles.filtros}>
        <div className={styles.filtroField}>
          <label className={styles.filtroLabel}>Data início</label>
          <input
            className={styles.filtroInput}
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
          />
        </div>
        <div className={styles.filtroField}>
          <label className={styles.filtroLabel}>Data fim</label>
          <input
            className={styles.filtroInput}
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
          />
        </div>
        <div className={styles.filtroField}>
          <label className={styles.filtroLabel}>Tipo</label>
          <select
            className={styles.filtroSelect}
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
          >
            <option value="">Todos</option>
            {Object.entries(TIPOS).map(([k, t]) => (
              <option key={k} value={k}>{t.label}</option>
            ))}
          </select>
        </div>
        <div className={styles.filtroField}>
          <label className={styles.filtroLabel}>Status</label>
          <select
            className={styles.filtroSelect}
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="EFETIVADA">Efetivada</option>
            <option value="EM_TRANSITO">Em trânsito</option>
            <option value="NEGADA">Negada</option>
          </select>
        </div>
        <div className={styles.filtroField}>
          <label className={styles.filtroLabel}>Buscar</label>
          <input
            className={styles.filtroInput}
            type="text"
            placeholder="Produto, OS, estoque..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>

        <div className={styles.btnExportar}>
          <button
            className={styles.btnExp}
            onClick={handlePDF}
            disabled={linhas.length === 0}
          >
            📄 PDF
          </button>
          <button
            className={styles.btnExp}
            onClick={handleExcel}
            disabled={linhas.length === 0}
          >
            📊 Excel
          </button>
        </div>
      </div>

      <div className={styles.tabelaWrap}>
        <div className={styles.tabelaHead}>
          <span className={styles.tabelaCount}>{linhas.length} registros</span>
        </div>
        <div className={styles.tabelaScroll}>
          <table className={styles.tabela}>
            <thead>
              <tr>
                <th>Data</th>
                <th>Tipo</th>
                <th>Produto</th>
                <th className={styles.textRight}>Qtd</th>
                <th>Origem</th>
                <th>Destino</th>
                <th>OS</th>
                <th>Status</th>
                <th>Responsável</th>
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr>
                  <td colSpan={9} className={styles.semResultados}>
                    Carregando...
                  </td>
                </tr>
              ) : linhas.length === 0 ? (
                <tr>
                  <td colSpan={9} className={styles.semResultados}>
                    Nenhuma movimentação no período/filtros.
                  </td>
                </tr>
              ) : (
                linhas.map((l, idx) => {
                  const t = TIPOS[l.tipo];
                  return (
                    <tr key={idx}>
                      <td className={styles.muted} style={{ whiteSpace: 'nowrap' }}>
                        {l.data}
                      </td>
                      <td>
                        <span
                          className={`${styles.badge} ${
                            styles[t?.classe || 'badgeNeutro']
                          }`}
                        >
                          {t?.label || l.tipo}
                        </span>
                      </td>
                      <td>
                        <strong>{l.produto}</strong>
                        {l.cor && (
                          <div className={styles.muted} style={{ fontSize: 10 }}>
                            {l.cor}
                          </div>
                        )}
                      </td>
                      <td className={styles.textRight}>
                        <strong>{l.qtd} L</strong>
                      </td>
                      <td>{l.origem || '—'}</td>
                      <td>{l.destino || '—'}</td>
                      <td className={styles.mono}>{l.os || '—'}</td>
                      <td>
                        <span
                          className={`${styles.badge} ${
                            l.status === 'EFETIVADA'
                              ? styles.badgeOk
                              : l.status === 'NEGADA'
                              ? styles.badgeWarn
                              : styles.badgeInfo
                          }`}
                        >
                          {l.status}
                        </span>
                      </td>
                      <td className={styles.muted}>{l.responsavel || '—'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
